import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allArticles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = allArticles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center py-20">
            <h1 className="font-heading font-bold text-2xl text-foreground-900">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = allArticles.filter((a) => a.id !== article.id).slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "@id": "https://51chigua.com/article/" + article.id + "/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "首页",
            "item": "https://51chigua.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": article.category,
            "item": "https://51chigua.com/category/" + article.categorySlug
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": article.title
          }
        ]
      },
      {
        "@type": "Article",
        "@id": "https://51chigua.com/article/" + article.id + "/#article",
        "headline": article.title,
        "description": article.excerpt,
        "image": article.image,
        "datePublished": article.date,
        "dateModified": article.date,
        "author": {
          "@type": "Person",
          "name": article.author
        },
        "publisher": {
          "@type": "Organization",
          "@id": "https://51chigua.com/#organization",
          "name": "51吃瓜App"
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://51chigua.com/article/" + article.id + "/#webpage"
        },
        "inLanguage": "zh-CN",
        "keywords": article.tags.join(", ")
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Navbar />
      <main className="pt-20 pb-10">
        <nav className="px-4 md:px-6 py-4 max-w-7xl mx-auto" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <li><Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors cursor-pointer">{article.category}</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-900 font-medium line-clamp-1">{article.title}</li>
          </ol>
        </nav>

        <article className="px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="lg:w-2/3">
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    {article.isExclusive && (
                      <span className="px-3 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-full whitespace-nowrap">
                        独家
                      </span>
                    )}
                    <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                      {article.category}
                    </span>
                    <span className="flex items-center gap-1 text-foreground-500 text-xs">
                      <i className="ri-fire-line text-accent-500 text-xs"></i>
                      {article.hotScore >= 10000
                        ? `${(article.hotScore / 10000).toFixed(1)}万热度`
                        : `${article.hotScore}热度`}
                    </span>
                  </div>

                  <h1 className="font-heading font-black text-2xl md:text-3xl lg:text-4xl text-foreground-900 leading-tight">
                    {article.title}
                  </h1>

                  <div className="flex items-center gap-4 mt-4 text-foreground-500 text-sm">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-user-line"></i>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-calendar-line"></i>
                      {article.date}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="relative w-full aspect-video rounded-2xl overflow-hidden mb-8">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="prose-custom space-y-5 text-foreground-800 leading-relaxed">
                  <p className="text-base md:text-lg">
                    <strong>据知情人士透露，</strong>{article.excerpt}
                  </p>
                  <p>
                    事件发生后，相关方迅速做出回应。据接近当事人的消息源表示，目前各方正在积极沟通协调中，具体情况有待进一步核实。业内人士分析认为，此次事件可能对相关方产生一定影响，但具体程度尚需观察后续发展。
                  </p>
                  <p>
                    值得注意的是，这并非娱乐圈第一次出现类似事件。回顾近年来的行业发展，类似的争议和爆料层出不穷，反映了娱乐圈生态的复杂性和多面性。有评论指出，随着社交媒体和自媒体平台的快速发展，信息传播的速度和广度都达到了前所未有的水平，这对公众人物的隐私保护和危机公关能力提出了更高的要求。
                  </p>
                  <p>
                    多位圈内人士在接受采访时表示，希望公众能够理性看待此类事件，在官方信息发布前不传谣不信谣。同时，也有声音呼吁行业建立更加规范和透明的信息披露机制，减少不实信息的传播。
                  </p>
                  <p>
                    截至目前，事件仍在持续发酵中。各大社交平台上，相关话题已登上热搜榜前列，累计阅读量超过数亿次。粉丝群体内部也出现了不同声音，部分粉丝表示支持偶像等待真相，也有部分粉丝表达了失望和质疑。
                  </p>
                  <p>
                    本平台将持续关注事件进展，第一时间为读者带来最新报道。欢迎下载51吃瓜App获取实时推送，不错过任何最新动态。
                  </p>
                </div>

                <div className="mt-10 p-6 rounded-2xl bg-background-100">
                  <h3 className="font-heading font-bold text-lg text-foreground-900 mb-4">
                    免责声明
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    本文内容来源于网络公开信息及网友投稿，本平台仅作整理和呈现，不对内容的真实性、准确性和完整性做任何保证。如涉及侵权或不实信息，请通过官方渠道联系我们进行核实和处理。
                  </p>
                </div>

                <div className="mt-10">
                  <h3 className="font-heading font-bold text-xl text-foreground-900 mb-6">
                    网友热评
                  </h3>
                  <div className="space-y-4">
                    {[
                      { name: '吃瓜不嫌事大', time: '2小时前', likes: 3256, content: '天呐！这也太劲爆了吧，坐等后续发展，这个瓜我吃定了！' },
                      { name: '理性看八卦', time: '3小时前', likes: 2189, content: '还是等官方通报吧，现在各种说法太多了，别急着站队。' },
                      { name: '娱乐圈小灵通', time: '4小时前', likes: 1567, content: '早就听说有这回事了，终于有人爆出来了，跟我听说的八九不离十。' },
                      { name: '佛系吃瓜人', time: '5小时前', likes: 892, content: '每次看到这种新闻都觉得贵圈真乱，我们普通人看看就好。' },
                    ].map((comment, idx) => (
                      <div key={idx} className="flex gap-3 p-4 rounded-xl bg-background-100">
                        <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-secondary-600 text-sm"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-foreground-900">{comment.name}</span>
                            <span className="text-foreground-500 text-xs">{comment.time}</span>
                          </div>
                          <p className="text-foreground-700 text-sm leading-relaxed">{comment.content}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <button className="flex items-center gap-1 text-foreground-500 text-xs hover:text-primary-500 transition-colors cursor-pointer">
                              <i className="ri-thumb-up-line"></i>
                              {comment.likes}
                            </button>
                            <button className="flex items-center gap-1 text-foreground-500 text-xs hover:text-primary-500 transition-colors cursor-pointer">
                              <i className="ri-chat-1-line"></i>
                              回复
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <aside className="lg:w-1/3">
                <div className="sticky top-24 space-y-6">
                  <div className="bg-background-100 rounded-2xl p-5">
                    <h4 className="font-heading font-bold text-base text-foreground-900 mb-4">
                      <i className="ri-share-forward-line mr-2 text-primary-500"></i>
                      分享文章
                    </h4>
                    <div className="flex gap-2">
                      {[
                        { icon: 'ri-weibo-line', label: '微博' },
                        { icon: 'ri-wechat-line', label: '微信' },
                        { icon: 'ri-qq-line', label: 'QQ' },
                        { icon: 'ri-link', label: '复制链接' },
                      ].map((share) => (
                        <button
                          key={share.label}
                          className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl bg-background-50 hover:bg-primary-50 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                          title={share.label}
                        >
                          <i className={`${share.icon} text-lg`}></i>
                          <span className="text-xs">{share.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl p-6 text-background-50">
                    <h4 className="font-heading font-bold text-lg mb-2">下载51吃瓜App</h4>
                    <p className="text-sm text-background-50/80 mb-4">
                      随时随地畅快吃瓜，爆料推送快人一步
                    </p>
                    <a
                      href="#download"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-50 text-primary-600 font-semibold text-sm rounded-full hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-download-2-line"></i>
                      立即下载
                    </a>
                  </div>

                  {relatedArticles.length > 0 && (
                    <div className="bg-background-100 rounded-2xl p-5">
                      <h4 className="font-heading font-bold text-base text-foreground-900 mb-4">相关文章</h4>
                      <div className="flex flex-col gap-3">
                        {relatedArticles.map((rel) => (
                          <Link
                            key={rel.id}
                            to={`/article/${rel.id}`}
                            className="flex gap-3 p-2 rounded-xl hover:bg-background-200/70 transition-colors cursor-pointer group"
                          >
                            <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                              <img
                                src={rel.image}
                                alt={rel.title}
                                className="w-full h-full object-cover object-top"
                                loading="lazy"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="font-heading font-semibold text-sm text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                                {rel.title}
                              </h5>
                              <p className="mt-1 text-foreground-500 text-xs">{rel.date}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}